import type { UserRole } from '@yhelp/shared-types';

export const TOUR_VERSION = '3';

export interface TourStep {
  target?: string;
  title: string;
  body: string;
  route?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  openFirstTicket?: boolean;
}

export interface TourDefinition {
  id: string;
  title: string;
  description: string;
  roles?: UserRole[];
  steps: TourStep[];
}

export const TOUR_CATALOG: TourDefinition[] = [
  {
    id: 'welcome',
    title: 'Boas-vindas',
    description: 'Visão geral do menu e navegação principal.',
    steps: [
      {
        title: 'Bem-vindo ao yHelp',
        body: 'Este tour rápido mostra onde encontrar chamados, dashboard e ajuda. Você pode fechar a qualquer momento.',
        route: '/app',
        placement: 'center',
      },
      {
        target: 'sidebar',
        title: 'Menu lateral',
        body: 'Navegue entre início, chamados, filas, notificações, dashboard e base de conhecimento.',
        route: '/app',
        placement: 'right',
      },
      {
        target: 'nav-tickets',
        title: 'Chamados',
        body: 'Central de tickets — abra, filtre e acompanhe o andamento.',
        route: '/tickets',
        placement: 'right',
      },
      {
        target: 'nav-notifications',
        title: 'Notificações',
        body: 'Alertas sobre comentários, status e atribuições aparecem aqui.',
        route: '/notifications',
        placement: 'right',
      },
      {
        target: 'help-fab',
        title: 'Central de ajuda',
        body: 'Acesse tours específicos, KB e configurações pelo botão de ajuda.',
        route: '/app',
        placement: 'left',
      },
    ],
  },
  {
    id: 'create-ticket',
    title: 'Criar chamado',
    description: 'Assunto, produto, classificação e anexos.',
    steps: [
      {
        target: 'ticket-create-btn',
        title: 'Novo chamado',
        body: 'Clique aqui para abrir o formulário completo de registro.',
        route: '/tickets',
        placement: 'bottom',
      },
      {
        target: 'ticket-create-form',
        title: 'Formulário',
        body: 'Preencha assunto, produto, classificação, descrição e prioridade.',
        route: '/tickets/new',
        placement: 'top',
      },
      {
        target: 'ticket-field-product',
        title: 'Produto',
        body: 'Selecione o produto ySec relacionado: Cloudflare, Okta, Auth0 ou yFace.',
        route: '/tickets/new',
        placement: 'top',
      },
      {
        target: 'ticket-field-classification',
        title: 'Classificação',
        body: 'Tags coloridas ajudam a priorizar: troubleshoot (urgente), melhoria, feedback, implementação ou outro.',
        route: '/tickets/new',
        placement: 'top',
      },
      {
        target: 'ticket-attachments',
        title: 'Anexos',
        body: 'Envie imagens (até 5 MB) ou vídeos (até 25 MB) nos formatos permitidos.',
        route: '/tickets/new',
        placement: 'top',
      },
    ],
  },
  {
    id: 'update-ticket',
    title: 'Atualizar chamado',
    description: 'Alterar status e acompanhar SLA.',
    roles: ['agent_n1', 'agent_n2', 'agent', 'tenant_admin', 'support_manager', 'superadmin'],
    steps: [
      {
        target: 'ticket-row-first',
        title: 'Abrir chamado',
        body: 'Selecione um chamado da lista para ver detalhes.',
        route: '/tickets',
        placement: 'bottom',
        openFirstTicket: true,
      },
      {
        target: 'ticket-status-field',
        title: 'Status',
        body: 'Agentes alteram o status conforme o progresso: aberto → em andamento → resolvido → fechado.',
        openFirstTicket: true,
        placement: 'left',
      },
      {
        target: 'ticket-details-panel',
        title: 'Painel lateral',
        body: 'Veja prioridade, produto, classificação e indicador de SLA.',
        openFirstTicket: true,
        placement: 'left',
      },
    ],
  },
  {
    id: 'close-ticket',
    title: 'Fechar chamado',
    description: 'Encerrar após resolução.',
    roles: ['agent_n1', 'agent_n2', 'agent', 'tenant_admin', 'support_manager', 'superadmin'],
    steps: [
      {
        target: 'ticket-close-btn',
        title: 'Fechar chamado',
        body: 'Quando resolvido, use este botão. O solicitante será notificado.',
        openFirstTicket: true,
        placement: 'top',
      },
    ],
  },
  {
    id: 'attachments',
    title: 'Anexos',
    description: 'Enviar imagens e vídeos com segurança.',
    steps: [
      {
        target: 'ticket-attachments',
        title: 'Anexos no chamado',
        body: 'Na criação ou no detalhe, adicione evidências visuais respeitando os limites de tamanho e tipo.',
        route: '/tickets/new',
        placement: 'top',
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Métricas e tendências da operação.',
    steps: [
      {
        target: 'nav-dashboard',
        title: 'Dashboard',
        body: 'Acesse indicadores de chamados abertos, resolvidos e SLA em risco.',
        route: '/dashboard',
        placement: 'right',
      },
      {
        target: 'dashboard-stats',
        title: 'Indicadores',
        body: 'Cards com números-chave e tendência dos últimos 7 dias.',
        route: '/dashboard',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'chat',
    title: 'Conversar no chamado',
    description: 'Chat flutuante e timeline.',
    steps: [
      {
        target: 'ticket-row-first',
        title: 'Detalhe do chamado',
        body: 'Abra um chamado para conversar com o suporte.',
        route: '/tickets',
        placement: 'bottom',
        openFirstTicket: true,
      },
      {
        target: 'ticket-timeline',
        title: 'Timeline',
        body: 'Histórico público de mensagens e eventos do chamado.',
        openFirstTicket: true,
        placement: 'top',
      },
      {
        target: 'floating-chat',
        title: 'Chat flutuante',
        body: 'Botão amarelo no canto inferior esquerdo — envie mensagens sem sair da página.',
        openFirstTicket: true,
        placement: 'right',
      },
    ],
  },
  {
    id: 'queues',
    title: 'Filas N1',
    description: 'Assumir chamados sem responsável.',
    roles: ['agent_n1', 'agent_n2', 'agent', 'tenant_admin', 'support_manager', 'superadmin'],
    steps: [
      {
        target: 'nav-queues',
        title: 'Filas',
        body: 'Chamados abertos aguardando triagem.',
        route: '/queues',
        placement: 'right',
      },
      {
        target: 'queue-list',
        title: 'Assumir',
        body: 'Clique em Assumir para atribuir o ticket a você.',
        route: '/queues',
        placement: 'bottom',
      },
    ],
  },
];

export function getTourById(id: string): TourDefinition | undefined {
  return TOUR_CATALOG.find((t) => t.id === id);
}

export function toursForRole(role: string): TourDefinition[] {
  return TOUR_CATALOG.filter((t) => !t.roles || t.roles.includes(role as UserRole));
}

export type OnboardingPrefs = {
  tourVersion?: string;
  welcomeSeenAt?: string | null;
  welcomeDismissed?: boolean;
  tours?: Record<string, { completedAt?: string | null; skipped?: boolean }>;
  completedAt?: string | null;
  skipped?: boolean;
};

export function shouldShowWelcome(onboarding: OnboardingPrefs | undefined): boolean {
  if (!onboarding) return true;
  if (onboarding.tourVersion !== TOUR_VERSION) return true;
  return !onboarding.welcomeSeenAt && !onboarding.welcomeDismissed;
}

export function isTourCompleted(onboarding: OnboardingPrefs | undefined, tourId: string): boolean {
  const entry = onboarding?.tours?.[tourId];
  return Boolean(entry?.completedAt || entry?.skipped);
}
